/**
 * DebugPanelContext — open/close state + selected product id for the
 * Parallel Calculation Debug Panel (Day 47, Decision 16). Admin-only;
 * the keyboard shortcut and the panel itself enforce the role guard
 * client-side, the calculate_breakdown_extras RPC enforces it server-side.
 */
/* eslint-disable react-refresh/only-export-components -- Provider + context
 * are intentionally co-located here. Same pattern as useSessionTabs.tsx
 * (CLAUDE_REFERENCE.md §6 TS migration patterns). The consumer hook
 * useDebugPanel lives in its own file (src/hooks/useDebugPanel.ts) so
 * Fast Refresh remains effective for the panel + section components.
 */
import { createContext, useCallback, useMemo, useState, type JSX, type ReactNode } from 'react';

export interface DebugPanelContextValue {
  open: boolean;
  selectedProductId: string | null;
  /** Set the product id externally — e.g. from a page that wants to pre-seed
   *  the panel before the user opens it (Products grid row click, Buying
   *  Session selected row, ProductDetail page). */
  setSelectedProductId: (productId: string | null) => void;
  openPanel: (productId?: string | null) => void;
  closePanel: () => void;
  togglePanel: () => void;
}

export const DebugPanelContext = createContext<DebugPanelContextValue | null>(null);

interface DebugPanelProviderProps {
  children: ReactNode;
}

export function DebugPanelProvider({ children }: DebugPanelProviderProps): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  const openPanel = useCallback((productId?: string | null) => {
    if (productId !== undefined) setSelectedProductId(productId ?? null);
    setOpen(true);
  }, []);

  const closePanel = useCallback(() => setOpen(false), []);

  const togglePanel = useCallback(() => setOpen((v) => !v), []);

  const value = useMemo<DebugPanelContextValue>(
    () => ({ open, selectedProductId, setSelectedProductId, openPanel, closePanel, togglePanel }),
    [open, selectedProductId, openPanel, closePanel, togglePanel]
  );

  return <DebugPanelContext.Provider value={value}>{children}</DebugPanelContext.Provider>;
}
